import { Dispatch } from "redux";
import {
  Organization,
  OrganizationAttributes,
  UserAttributes,
  User,
} from "typings";
import { Users, Organizations } from "@pacerpro/api";

type DrawerState = "admin" | "office";

/////////////////////////////////////////////////
// ORGANIZATION CONSTANTS
/////////////////////////////////////////////////

export const SET_ORGANIZATION = "SET_ORGANIZATION";
export const SET_ORGANIZATION_DRAWER = "SET_ORGANIZATION_DRAWER";
export const SET_SELECTED_ADMIN = "SET_SELECTED_ADMIN";
export const EDIT_INCLUDED = "EDIT_INCLUDED";
export const REMOVE_INCLUDED = "REMOVE_INCLUDED";
export const ADD_INCLUDED = "ADD_INCLUDED";

/////////////////////////////////////////////////
// ORGANIZATION ACTIONS
/////////////////////////////////////////////////

export const setOrganization = ({
  data,
  included = [],
}: {
  data: Organization;
  included: any[];
}) => ({
  type: SET_ORGANIZATION,
  payload: { data, included },
});

export const setOrganizationDrawer = (payload?: DrawerState) => ({
  type: SET_ORGANIZATION_DRAWER,
  payload,
});

export const editOrganization = (
  organizationId: string,
  organizationAttributes: OrganizationAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const orgResponse = await Organizations.edit(
      organizationId,
      organizationAttributes,
      { include: ["offices", "users"] },
    );
    dispatch(setOrganization(orgResponse.data));
  } catch (error) {
    console.error(error);
  }
};

export const setSelectedAdmin = ({
  data,
  included = [],
}: {
  data?: User;
  included: any[];
}) => ({
  type: SET_SELECTED_ADMIN,
  payload: { data, included },
});

export const createAdmin = (userId: string) => async (dispatch: Dispatch) => {
  try {
    const adminResponse = await Users.edit(userId, {
      role: "owner",
    });
    dispatch(setSelectedAdmin(adminResponse.data));
    dispatch({
      type: ADD_INCLUDED,
      payload: adminResponse.data.data,
    });
  } catch (error) {
    console.error(error);
  }
};

export const removeAdmin = (userId: string) => async (dispatch: Dispatch) => {
  try {
    const adminResponse = await Users.edit(userId, {
      role: "member",
    });
    dispatch({
      type: REMOVE_INCLUDED,
      payload: adminResponse.data.data.id,
    });
  } catch (error) {
    console.error(error);
  }
};

export const editAdmin = (userId: string, attributes: UserAttributes) => async (
  dispatch: Dispatch,
) => {
  const adminResponse = await Users.edit(userId, attributes);
  dispatch(setSelectedAdmin(adminResponse.data));
  dispatch({
    type: EDIT_INCLUDED,
    payload: adminResponse.data.data,
  });
};

export const editAdminInTable = (
  userId: string,
  attributes: UserAttributes,
) => async (dispatch: Dispatch) => {
  try {
    const adminResponse = await Users.edit(userId, attributes);
    dispatch({
      type: EDIT_INCLUDED,
      payload: adminResponse.data.data,
    });
  } catch (error) {
    console.error(error);
  }
};

export type OrganizationAction =
  | {
      type: typeof SET_ORGANIZATION;
      payload: {
        data: Organization;
        included: any[];
      };
    }
  | {
      type: typeof SET_ORGANIZATION_DRAWER;
      payload?: DrawerState;
    }
  | {
      type: typeof SET_SELECTED_ADMIN;
      payload: {
        data?: User;
        included: any[];
      };
    }
  | {
      type: typeof EDIT_INCLUDED;
      payload: User;
    }
  | {
      type: typeof REMOVE_INCLUDED;
      payload: string;
    }
  | {
      type: typeof ADD_INCLUDED;
      payload: User;
    };

/////////////////////////////////////////////////
// ORGANIZATION REDUCER
/////////////////////////////////////////////////

export interface OrganizationState {
  organizations: {
    data: Organization[];
    included: any[];
  };
  organization: {
    data: Organization;
    included: any[];
  };
  admin: {
    data?: User;
    included: any[];
  };
  drawer?: DrawerState;
}

const initialState: OrganizationState = {
  organizations: {
    data: [],
    included: [],
  },
  organization: {
    data: {
      type: "organizations",
      id: "",
      attributes: {
        name: "",
        primaryContactEmail: "",
        primaryContactName: "",
        primaryContactPhone: "",
        primaryContactLocation: "",
      },
      relationships: {
        distributionLists: {
          links: {
            self: "",
          },
        },
        users: {
          links: {
            self: "",
          },
        },
        offices: {
          links: {
            self: "",
          },
        },
      },
      links: {
        self: "",
      },
    },
    included: [],
  },
  admin: {
    data: undefined,
    included: [],
  },
  drawer: undefined,
};

export default (state = initialState, action: OrganizationAction) => {
  switch (action.type) {
    case SET_ORGANIZATION: {
      return {
        ...state,
        organization: {
          data: action.payload.data,
          included: action.payload.included,
        },
      };
    }
    case SET_ORGANIZATION_DRAWER: {
      return {
        ...state,
        drawer: action.payload,
      };
    }
    case SET_SELECTED_ADMIN: {
      return {
        ...state,
        admin: action.payload,
      };
    }
    case EDIT_INCLUDED: {
      return {
        ...state,
        organization: {
          ...state.organization,
          included:
            state.organization.included &&
            state.organization.included.map((related: any) =>
              related.id === action.payload.id ? action.payload : related,
            ),
        },
      };
    }
    case ADD_INCLUDED: {
      return {
        ...state,
        organization: {
          ...state.organization,
          included: state.organization.included && [
            ...state.organization.included,
            action.payload,
          ],
        },
      };
    }
    case REMOVE_INCLUDED: {
      return {
        ...state,
        organization: {
          ...state.organization,
          included:
            state.organization.included &&
            state.organization.included.filter(
              (related: any) => related.id !== action.payload,
            ),
        },
      };
    }
    default:
      return state;
  }
};
